// Otimização de rota comercial (TSP heurístico): nearest-neighbor + 2-opt sobre
// distâncias haversine. Para roteiros urbanos de até ~25 paradas a ordem resultante
// é praticamente a mesma de um roteador rodoviário, sem dependência externa.

export interface RoutePoint {
    id: string;
    lat: number;
    lng: number;
    label: string;
}

export interface OptimizedRoute<T extends RoutePoint = RoutePoint> {
    order: T[];
    totalKm: number;
}

const EARTH_KM = 6371;

export const haversineKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const la1 = (a.lat * Math.PI) / 180;
    const la2 = (b.lat * Math.PI) / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_KM * Math.asin(Math.sqrt(h));
};

const routeLength = (pts: Array<{ lat: number; lng: number }>): number => {
    let d = 0;
    for (let i = 0; i < pts.length - 1; i++) d += haversineKm(pts[i], pts[i + 1]);
    return d;
};

/**
 * Ordena as paradas a partir de `origin` (se houver) minimizando a distância total.
 * A origem não entra na lista retornada — apenas ancora o ponto de partida.
 *
 * Com `destino`, a última perna (última parada → chegada) entra na conta e o 2-opt
 * nunca mexe nela: sem isso a rota terminaria longe de onde a pessoa disse que vai
 * acabar o dia. O destino também não entra na lista retornada.
 */
export function optimizeRoute<T extends RoutePoint>(
    points: T[],
    origin?: { lat: number; lng: number },
    destino?: { lat: number; lng: number } | null,
): OptimizedRoute<T> {
    if (points.length <= 1) {
        const pernas = [origin, ...points, destino].filter(Boolean) as Array<{ lat: number; lng: number }>;
        return { order: [...points], totalKm: points.length === 1 ? routeLength(pernas) : 0 };
    }

    // 1) Nearest-neighbor
    const remaining = [...points];
    const order: T[] = [];
    let current: { lat: number; lng: number } = origin ?? remaining[0];
    if (!origin) order.push(remaining.shift()!);

    while (remaining.length > 0) {
        let bestIdx = 0;
        let bestDist = Infinity;
        for (let i = 0; i < remaining.length; i++) {
            const d = haversineKm(current, remaining[i]);
            if (d < bestDist) { bestDist = d; bestIdx = i; }
        }
        const next = remaining.splice(bestIdx, 1)[0];
        order.push(next);
        current = next;
    }

    // 2) Melhoria 2-opt (com origem fixa no início, se houver)
    // O destino fica no fim do caminho: o laço abaixo nunca move o último ponto
    // (j < path.length - 1), então ele só serve de âncora para a última perna.
    const path: Array<{ lat: number; lng: number }> = [
        ...(origin ? [origin] : []), ...order, ...(destino ? [destino] : []),
    ];
    const fixedOffset = origin ? 1 : 0;
    let improved = true;
    let guard = 0;
    while (improved && guard < 50) {
        improved = false;
        guard++;
        for (let i = fixedOffset; i < path.length - 2; i++) {
            for (let j = i + 1; j < path.length - 1; j++) {
                const before = haversineKm(path[i - 1] ?? path[i], path[i]) + haversineKm(path[j], path[j + 1]);
                const after = haversineKm(path[i - 1] ?? path[i], path[j]) + haversineKm(path[i], path[j + 1]);
                if (i > 0 && after < before - 1e-9) {
                    // inverte o trecho i..j
                    const segment = path.slice(i, j + 1).reverse();
                    path.splice(i, segment.length, ...segment);
                    improved = true;
                }
            }
        }
    }

    // Reconstrói a ordem tipada a partir do caminho otimizado
    const final: T[] = [];
    // Sem o destino: uma parada no mesmo ponto da chegada seria "achada" duas vezes.
    for (const p of path.slice(fixedOffset, destino ? path.length - 1 : undefined)) {
        const found = order.find(o => o.lat === (p as any).lat && o.lng === (p as any).lng && !final.includes(o));
        if (found) final.push(found);
    }

    return { order: final, totalKm: routeLength(path) };
}

/** O Google Maps aceita no máximo 9 waypoints + destino = 10 paradas por rota — limite
 *  do PRODUTO (vale até adicionando parada manualmente no app); acima disso ele corta
 *  em silêncio (rota de 102 paradas aparecia com 9). Não há como abrir tudo de uma vez. */
export const GOOGLE_MAPS_MAX_STOPS = 10;

const dirUrl = (
    coords: string[],
    origin?: { lat: number; lng: number },
    /** Chegada explícita. Sem ela, a última parada faz as vezes de destino (rota aberta). */
    destino?: { lat: number; lng: number } | null,
): string => {
    const destination = destino ? `${destino.lat},${destino.lng}` : coords[coords.length - 1];
    const waypoints = (destino ? coords : coords.slice(0, -1)).join('|');
    const params = new URLSearchParams({ api: '1', destination, travelmode: 'driving' });
    if (origin) params.set('origin', `${origin.lat},${origin.lng}`);
    if (waypoints) params.set('waypoints', waypoints);
    return `https://www.google.com/maps/dir/?${params.toString()}`;
};

/**
 * Link de navegação no Google Maps com as próximas paradas na ordem otimizada
 * (limitado a GOOGLE_MAPS_MAX_STOPS — conforme visitas concluem, o link avança).
 *
 * Com `destino`, a chegada ocupa o lugar de `destination` e TODAS as paradas viram
 * waypoints — por isso o teto de paradas cai em um: o destino consome uma das 10 vagas
 * do Maps. Sem isso o vendedor era levado até a última empresa da lista, não até onde
 * ele disse que ia terminar o dia.
 */
export const googleMapsDirLink = (
    points: Array<{ lat: number; lng: number }>,
    origin?: { lat: number; lng: number },
    destino?: { lat: number; lng: number } | null,
): string => {
    if (points.length === 0) return '';
    const teto = destino ? GOOGLE_MAPS_MAX_STOPS - 1 : GOOGLE_MAPS_MAX_STOPS;
    return dirUrl(points.slice(0, teto).map(p => `${p.lat},${p.lng}`), origin, destino);
};

/** Navegação para UM destino — sem limite de paradas; sem `origin` o Maps parte da
 *  localização atual do aparelho (no celular já entra direto em modo direção). */
export const googleMapsNavUrl = (p: { lat: number; lng: number }): string =>
    `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}&travelmode=driving`;

/**
 * Navegação para um endereço em TEXTO, para quando a coordenada guardada não é do
 * endereço do cliente (`geocode_status` diferente de 'endereco').
 *
 * Mandar a coordenada nesse caso leva o vendedor a um ponto que ninguém prometeu:
 * no nível 'cidade' o pino fica no centro do município, e havia clientes de Goiânia
 * a 8 km do endereço real. Com o endereço em texto quem resolve é o Google na hora
 * — costuma achar mais que o nosso geocoder — e, quando não acha, o vendedor pelo
 * menos LÊ o endereço na tela do Maps em vez de dirigir até um pino silenciosamente
 * errado.
 */
export const googleMapsNavUrlEndereco = (endereco: string): string =>
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(endereco)}&travelmode=driving`;

export interface MapsTrecho { ini: number; fim: number; url: string }

/**
 * Rota completa dividida em trechos contínuos de até 10 paradas: a última parada de um
 * trecho é a primeira do seguinte, então dá pra navegar a rota inteira encadeando os
 * links — é o máximo que o Google Maps permite por link.
 *
 * Com `destino`, ele entra APENAS no último trecho, como chegada. Nos trechos do meio a
 * última parada continua sendo o destino do link (é o ponto de emenda com o próximo) —
 * enfiar a chegada em todos mandaria o motorista atravessar a cidade a cada trecho.
 * Como o destino ocupa uma vaga, o último trecho leva 9 paradas em vez de 10.
 */
/**
 * As `n` paradas mais perto umas das outras, na ordem do caminho: parte da
 * primeira da fila e vai sempre para a mais perto da última escolhida.
 *
 * "Visitar 5" cortava a ordem da rota nas 5 primeiras, e a ordem de uma rota
 * que cobre o estado dá a volta nele: na de 16/09/2026 as 5 primeiras eram
 * Trindade, Aparecida, Aparecida, Goiatuba e Pires do Rio — 559 km. Vizinhança
 * pega as 5 que dá para fazer num dia.
 *
 * Parada sem ponto não tem como ser medida: só entra se faltar parada com ponto
 * para completar `n`, na ordem em que estava.
 */
export const escolherVizinhas = (
    /** Na ordem atual da rota. */
    paradas: Array<{ id: string; ponto: { lat: number; lng: number } | null }>,
    n: number,
): string[] => {
    if (n >= paradas.length) return paradas.map(p => p.id);
    const escolhidas: string[] = [];
    const comPonto = paradas.filter((p): p is { id: string; ponto: { lat: number; lng: number } } => !!p.ponto);
    if (comPonto.length > 0 && n > 0) {
        let atual = comPonto[0];
        const restantes = comPonto.slice(1);
        escolhidas.push(atual.id);
        while (escolhidas.length < n && restantes.length > 0) {
            let i = 0;
            for (let j = 1; j < restantes.length; j++) {
                if (haversineKm(atual.ponto, restantes[j].ponto) < haversineKm(atual.ponto, restantes[i].ponto)) i = j;
            }
            atual = restantes.splice(i, 1)[0];
            escolhidas.push(atual.id);
        }
    }
    for (const p of paradas) {
        if (escolhidas.length >= n) break;
        if (!p.ponto) escolhidas.push(p.id);
    }
    return escolhidas;
};

export const googleMapsDirTrechos = (
    points: Array<{ lat: number; lng: number }>,
    destino?: { lat: number; lng: number } | null,
): MapsTrecho[] => googleMapsTrechosDeAlvos(points.map(p => `${p.lat},${p.lng}`), destino);

/**
 * Os mesmos trechos, com cada parada já como o Maps a entende: "lat,lng" ou um
 * endereço em texto. O texto existe para a parada sem pino confiável — antes
 * ela ficava fora do link sem aviso, e 20 paradas viravam um link de 9
 * (rota de cobrança de 16/09/2026: 13 das 24 pendentes sem pino).
 *
 * `emendar: false` tira a repetição da emenda. Sem `origin`, o Maps parte de
 * onde o aparelho está — quem abre o trecho 2 ao terminar o 1 já está na última
 * parada dele, e repeti-la só gastava uma das 10 vagas. É o que faz 20 paradas
 * caberem em dois links de 10, e não em três.
 */
export const googleMapsTrechosDeAlvos = (
    alvos: string[],
    destino?: { lat: number; lng: number } | null,
    { emendar = true }: { emendar?: boolean } = {},
): MapsTrecho[] => {
    const trechos: MapsTrecho[] = [];
    // "|" separa as paradas na URL: dentro de um endereço, quebraria a rota.
    const limpos = alvos.map(a => a.replace(/\|/g, ' '));
    if (limpos.length === 0) return trechos;
    let inicio = 0;
    for (;;) {
        // O trecho que fecha a rota cede uma vaga para a chegada
        const ehUltimo = inicio + GOOGLE_MAPS_MAX_STOPS >= limpos.length;
        const teto = ehUltimo && destino ? GOOGLE_MAPS_MAX_STOPS - 1 : GOOGLE_MAPS_MAX_STOPS;
        const fatia = limpos.slice(inicio, inicio + teto);
        const fechaAqui = destino && inicio + teto >= limpos.length;
        trechos.push({
            ini: inicio + 1,
            fim: inicio + fatia.length,
            url: dirUrl(fatia, undefined, fechaAqui ? destino : null),
        });
        if (inicio + teto >= limpos.length) break;
        inicio += emendar ? teto - 1 : teto;
    }
    return trechos;
};
